import { DateFormatFamilyEnum } from '../../../../constants';
import { DateFormatPreference } from '../../../../store/mst/models/DateFormatPreferenceEnum';
import { AnalyticsEventsProps } from '../../../../analytics/analyticsEventsProps';

export type DateFormatFamily = DateFormatFamilyEnum;
export type CSVDateFormatAnalytics = AnalyticsEventsProps.Order.CSVDateFormatDetectedEventProps;

/**
 * Builds analytics data based on actual date parsing results.
 * This is called after all dates have been parsed to capture real parsing statistics.
 *
 * @param parsedFormatFamilies - Array of format families that were actually used to parse each date
 * @param detectedFormat - The detected/preferred format family
 * @param orgDateFormatPreference - Organization's date format preference setting
 * @returns Analytics data ready to send to tracking service
 */
export const buildDateFormatAnalyticsFromParseResults = (
  parsedFormatFamilies: (DateFormatFamily | null)[],
  deliveryDateErrorCount: number,
  detectedFormat: DateFormatFamily,
  orgDateFormatPreference: DateFormatPreference,
): CSVDateFormatAnalytics => {
  const totalOrders = parsedFormatFamilies.length + deliveryDateErrorCount;
  const successfullyParsed = parsedFormatFamilies.filter((f) => f !== null).length;

  // Count how many dates were actually parsed with each format family
  const mmFirstCount = parsedFormatFamilies.filter((f) => f === DateFormatFamilyEnum.MM_FIRST).length;
  const ddFirstCount = parsedFormatFamilies.filter((f) => f === DateFormatFamilyEnum.DD_FIRST).length;
  const yyyyFirstCount = parsedFormatFamilies.filter((f) => f === DateFormatFamilyEnum.YYYY_FIRST).length;

  // Calculate percentages based on actual parsing results
  const percentMonthFirst = totalOrders > 0 ? Math.round((mmFirstCount / totalOrders) * 100) : 0;
  const percentDayFirst = totalOrders > 0 ? Math.round((ddFirstCount / totalOrders) * 100) : 0;
  const percentYearFirst = totalOrders > 0 ? Math.round((yyyyFirstCount / totalOrders) * 100) : 0;
  const percentInvalid = totalOrders > 0 ? Math.round((deliveryDateErrorCount / totalOrders) * 100) : 0;

  // Determine result type
  let resultType: string;
  if (orgDateFormatPreference && orgDateFormatPreference !== DateFormatPreference.AUTO) {
    resultType = 'Organization preference used';
  } else if (successfullyParsed === 0) {
    resultType = 'Fallback to browser locale';
  } else if (mmFirstCount === ddFirstCount) {
    resultType = 'Fallback to browser locale';
  } else {
    resultType = 'Detection succeeded';
  }

  // Map detected format to format string
  const formatMap: Record<DateFormatFamily, string> = {
    [DateFormatFamilyEnum.MM_FIRST]: 'MMDDYYYY',
    [DateFormatFamilyEnum.DD_FIRST]: 'DDMMYYYY',
    [DateFormatFamilyEnum.YYYY_FIRST]: 'YYYYMMDD',
  };

  return {
    totalOrdersWithDeliveryDate: totalOrders,
    percentOrdersWithMonthFirst: percentMonthFirst,
    percentOrdersWithDayFirst: percentDayFirst,
    percentOrdersWithYearFirst: percentYearFirst,
    percentOrdersWithInvalidDate: percentInvalid,
    preferredDateFormat: formatMap[detectedFormat],
    resultType,
  };
};
